import React, {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

const STATUS_PILL_DURATION_MS = 4000

export interface StatusPillStatus {
  id: number
  label: string
}

export interface StatusPillContextType {
  status?: StatusPillStatus
  /** delayMs lets a navigation finish first, so the pill lands on the screen the user ends up on. */
  showStatus: (label: string, delayMs?: number) => void
}

// Default keeps showStatus safe to call in windows that don't mount the provider.
export const StatusPillContext = createContext<StatusPillContextType>({ showStatus: () => {} })

/**
 * Holds the "Swap Completed"-style pill. It sits above the router so it survives navigation; the
 * pill itself is rendered by RootLayout, over the content column.
 */
const StatusPillProvider: FC<PropsWithChildren> = ({ children }) => {
  const [status, setStatus] = useState<StatusPillStatus>()
  const delayRef = useRef<NodeJS.Timeout>()

  const showStatus = useCallback((label: string, delayMs = 0) => {
    clearTimeout(delayRef.current)
    delayRef.current = setTimeout(() => setStatus({ id: Date.now(), label }), delayMs)
  }, [])

  useEffect(() => () => clearTimeout(delayRef.current), [])

  useEffect(() => {
    if (!status) return
    const timeout = setTimeout(() => setStatus(undefined), STATUS_PILL_DURATION_MS)
    return () => clearTimeout(timeout)
  }, [status])

  const value = useMemo(() => ({ status, showStatus }), [status, showStatus])

  return <StatusPillContext.Provider value={value}>{children}</StatusPillContext.Provider>
}

export default StatusPillProvider
